import { useLoading } from "components/shared/GlobalLoading";
import { Notification, Spinner, toast } from "components/ui";
import {
  BANK,
  BANK_CABANG,
  INSURANCE,
} from "constants/roles.constant";
import dayjs from "dayjs";
import { get, noop } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { HiUser } from "react-icons/hi";
import { IoMdDownload } from "react-icons/io";
import { MdOutlineFileCopy } from "react-icons/md";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { deleteChat, getChatList } from "services/ChatService";
import { getFileChatClaim } from "services/ClaimService";
import { apiUpdateCounterChat } from "services/NewBusiness";
import downloadBase64 from "utils/downloadBase64";
import InputChat from "./InputChat";

const TabByRole = {
  [INSURANCE]: BANK_CABANG,
  [INSURANCE]: BANK,
  [BANK_CABANG]: INSURANCE,
  [BANK]: INSURANCE,
};

const handleDownloadFile = async (
  fileName,
  downloadingFiles,
  setDownloadingFiles
) => {
  if (!fileName) {
    toast.push(
      <Notification type="danger" duration={2500}>
        Nama file tidak valid
      </Notification>,
      {
        placement: "top-end",
      }
    );
    return;
  }

  try {
    setDownloadingFiles((prev) => ({ ...prev, [fileName]: true }));
    const resp = await getFileChatClaim(fileName);
    const fileData = resp?.data?.data;

    if (fileData?.base64) {
      downloadBase64(fileData.base64, fileName);
      toast.push(
        <Notification type="success" duration={2500}>
          File berhasil diunduh
        </Notification>,
        {
          placement: "top-end",
        }
      );
    } else {
      throw new Error("File data tidak ditemukan");
    }
  } catch (e) {
    console.error("Error downloading file:", e);
    toast.push(
      <Notification type="danger" duration={2500}>
        {e?.message || "Gagal mengunduh file"}
      </Notification>,
      {
        placement: "top-end",
      }
    );
  } finally {
    setDownloadingFiles((prev) => {
      const newState = { ...prev };
      delete newState[fileName];
      return newState;
    });
  }
};

const ChatBox = ({ id, claimData }) => {
  const userData = useSelector((state) => state.auth.user);
  const userRole = userData?.authority?.[0] || "";
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [activeTab, setActiveTab] = useState(TabByRole[userRole] || null);
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const showSidebar = userRole === INSURANCE;

  // For non-INSURANCE users, use the default tab mapping
  const currentTab = showSidebar ? activeTab : TabByRole[userRole];

  const getCreatedTo = () => {
    if (!claimData) return null;

    switch (currentTab) {
      case INSURANCE:
        return claimData?.insurance_id || null;
      case BANK_CABANG:
        return claimData?.cabang_id || null;
      case BANK:
        return claimData?.pusat_id || null;
      default:
        return null;
    }
  };

  const payloadChat = {
    user_login: userData?.user || null,
    created_by: userData?.user || null,
    created_to: getCreatedTo(),
    id_card_number: claimData?.id_card_number || null,
  };

  const handleUpdateCounterChat = useCallback(async () => {
    try {
      if (claimData?.id_card_number !== undefined) {
        const response = await apiUpdateCounterChat({
          id_card_number: claimData?.id_card_number,
          user_id: userData?.user,
        });

        const result = get(response, "data.data", {});

        return result;
      }
    } catch (error) {
      toast.push(
        <Notification type="danger" duration={2500}>
          {error.message}
        </Notification>,
        {
          placement: "top-end",
        }
      );
    }
  }, [claimData?.id_card_number, userData?.user]);

  const {
    data: chatData,
    isLoading: loading,
    refetch,
  } = useQuery(
    ["getChatList", payloadChat],
    async ({ queryKey }) => {
      const [, params] = queryKey;
      try {
        const response = await getChatList(params);
        return get(response, "data.data", {});
      } catch (e) {
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const fetchChatByTab = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (!claimData?.id_card_number) return;

    let isMounted = true;

    const updateCounter = async () => {
      if (isMounted) {
        await handleUpdateCounterChat();
      }
    };

    updateCounter();

    return () => {
      isMounted = false;
    };
  }, [claimData?.id_card_number, handleUpdateCounterChat]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await fetchChatByTab();
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [currentTab, fetchChatByTab]);

  const handleDelete = async (id) => {
    showLoadingSpinner();
    try {
      await deleteChat(id);
      await refetch();

      toast.push(
        <Notification title="Success" type="success" duration={2500}>
          Berhasil menghapus pesan!
        </Notification>,
        {
          placement: "top-end",
        }
      );
    } catch (error) {
      hideLoadingSpinner();
      let message = get(error, "response.data.message");
      message =
        message || get(error, "response.data.data", "Terjadi kesalahan");

      toast.push(
        <Notification title="Error" type="danger">
          {message}
        </Notification>,
        {
          placement: "top-end",
        }
      );
    }
    hideLoadingSpinner();
  };

  // Restrict chat access to only BANK, BANK_CABANG and INSURANCE roles
  if (![BANK, BANK_CABANG, INSURANCE].includes(userRole)) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border">
        <div className="text-center">
          <p className="text-gray-500 text-sm">
            Fitur chat hanya tersedia untuk role Asuransi, Bank Pusat, dan Bank Cabang
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {showSidebar ? (
        <div className="flex gap-6">
          <div className="w-1/3">
            <Sidebar
              claimData={claimData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
          <div className="flex-1">
            <TabContent
              activeTab={currentTab}
              claimId={id}
              claimData={claimData}
              chatData={chatData}
              userId={userData.user}
              getCreatedTo={getCreatedTo}
              loading={loading}
              handleDelete={handleDelete}
              refetch={refetch}
              downloadingFiles={downloadingFiles}
              setDownloadingFiles={setDownloadingFiles}
            />
          </div>
        </div>
      ) : (
        <TabContent
          activeTab={currentTab}
          claimId={id}
          claimData={claimData}
          chatData={chatData}
          userId={userData.user}
          getCreatedTo={getCreatedTo}
          loading={loading}
          handleDelete={handleDelete}
          refetch={refetch}
          downloadingFiles={downloadingFiles}
          setDownloadingFiles={setDownloadingFiles}
        />
      )}
    </div>
  );
};

const ChatContent = (props) => {
  const {
    claimData,
    claimId,
    chatData,
    userId,
    createdTo,
    loading,
    handleDelete,
    refetch,
    downloadingFiles,
    setDownloadingFiles,
  } = props;
  const { title, description } = getChatHeaderLabel({
    activeTab: props.activeTab,
    data: claimData,
  });
  return (
    <div>
      <div className="flex justify-start gap-4 mx-8 pt-5">
        <div className="flex items-center justify-center w-12 h-12 bg-[#D9D9D9] rounded-full">
          <HiUser className="text-2xl text-[#737373]" />
        </div>
        <div>
          <h6>{title}</h6>
          <span>{description}</span>
        </div>
      </div>
      <hr className="my-4" />
      <div
        className="p-2 sm:p-4 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg border border-gray-200 shadow-inner"
        style={{ height: "500px" }}
      >
        <div
          id="messages"
          className="flex flex-col p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 transition-colors flex-1"
          style={{
            minHeight: "300px",
            maxHeight: "450px",
            background: "linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)",
          }}
        >
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Spinner className="mx-auto" size={50} />
            </div>
          ) : Array.isArray(chatData) && chatData.length > 0 ? (
            chatData.map((item) => {
              return renderMessage({
                item,
                userId,
                createdTo,
                handleDelete,
                downloadingFiles,
                setDownloadingFiles,
              });
            })
          ) : (
            <div className="flex justify-center items-center h-40 text-gray-500">
              <p className="text-sm">Belum ada pesan. Mulai percakapan!</p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <InputChat
          claim_id={claimId}
          user_id={userId}
          created_to={createdTo}
          idCardNumber={claimData?.id_card_number || null}
          refetch={refetch}
        />
      </div>
    </div>
  );
};

const Sidebar = ({ claimData, activeTab, setActiveTab }) => {
  return (
    <div className="flex-col space-y-4">
      <h6>Chat</h6>
      {[BANK_CABANG, BANK].map((tab) => (
        <SidebarItem
          key={tab}
          tab={tab}
          label={
            tab === BANK_CABANG
              ? claimData?.nama_cabang || "Bank Cabang"
              : claimData?.nama_pusat || "Bank Pusat"
          }
          description={
            tab === BANK_CABANG ? "Bank Cabang" : "Bank Pusat"
          }
          activeTab={activeTab === tab ? true : false}
          setActiveTab={setActiveTab}
        />
      ))}
    </div>
  );
};

const SidebarItem = ({ label, tab, description, activeTab, setActiveTab }) => {
  return (
    <div
      onClick={() => setActiveTab(tab)}
      className={`rounded px-3 py-4 cursor-pointer ${activeTab === true
        ? "border-l-4 border-l-blue-600 bg-blue-100"
        : "bg-[#FAFAFA] border"
        }`}
    >
      <h6 className="mb-2">{label}</h6>
      <span>{description}</span>
    </div>
  );
};

const TabContent = ({
  activeTab,
  claimId,
  chatData,
  claimData,
  userId,
  handleDelete,
  loading,
  getCreatedTo,
  refetch,
  downloadingFiles,
  setDownloadingFiles,
}) => {
  return (
    <div className="w-full">
      <ChatContent
        activeTab={activeTab}
        claimData={claimData}
        chatData={chatData}
        claimId={claimId}
        userId={userId}
        createdTo={getCreatedTo()}
        loading={loading}
        handleDelete={handleDelete}
        refetch={refetch}
        downloadingFiles={downloadingFiles}
        setDownloadingFiles={setDownloadingFiles}
      />
    </div>
  );
};

const getChatHeaderLabel = ({ activeTab, data }) => {
  if (!data) {
    return {
      title: "Unknown",
      description: "No data available",
    };
  }

  switch (activeTab) {
    case INSURANCE:
      return {
        title: data?.insurance_name || "Unknown Insurance",
        description: "Perusahaan Asuransi",
      };
    case BANK_CABANG:
      return {
        title: data?.nama_cabang,
        description: "Bank Cabang",
      };
    case BANK:
      return {
        title: data?.nama_pusat || "Bank Pusat",
        description: "Bank Pusat",
      };
    default:
      return {
        title: "Unknown",
        description: "Unknown Type",
      };
  }
};

const renderMessage = ({
  item,
  userId,
  createdTo,
  handleDelete = noop,
  downloadingFiles,
  setDownloadingFiles,
}) => {
  // Helper untuk render file attachment
  const renderFileAttachment = (isOwn = false) => {
    if (!item.file) return null;

    const isDownloading = downloadingFiles[item.file] || false;
    const fileContainerClass = isOwn
      ? "bg-gradient-to-r from-sky-50 to-sky-100 border-sky-200"
      : "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200";

    return (
      <div
        className={`flex flex-row items-center gap-3 border rounded-lg p-3 mb-2 ${fileContainerClass} shadow-sm`}
      >
        <div className="p-2 bg-white rounded-md shadow-sm">
          <MdOutlineFileCopy
            size={20}
            className={isOwn ? "text-sky-600" : "text-gray-600"}
          />
        </div>
        <div className="flex-1">
          <h6 className="font-semibold text-sm text-gray-800">{item.file}</h6>
          <p className="text-xs text-gray-500">
            {item.file_size || "Unknown size"}
          </p>
        </div>
        <button
          className={`min-w-[36px] h-[36px] flex justify-center items-center rounded-md transition-all duration-200 ${isDownloading
            ? "bg-gray-400 cursor-not-allowed opacity-75"
            : isOwn
              ? "bg-sky-500 hover:bg-sky-600 text-white"
              : "bg-gray-500 hover:bg-gray-600 text-white"
            }`}
          onClick={() =>
            !isDownloading &&
            handleDownloadFile(item.file, downloadingFiles, setDownloadingFiles)
          }
          disabled={isDownloading}
          title={isDownloading ? "Mengunduh..." : "Download file"}
        >
          {isDownloading ? (
            <Spinner size={16} className="text-white" />
          ) : (
            <IoMdDownload size={16} />
          )}
        </button>
      </div>
    );
  };

  // Pesan dari user yang sedang login (Pengirim)
  if (item?.created_by?.id === userId) {
    return (
      <div key={`${item.uuid}`} className="chat-message mb-4">
        <div className="flex items-end justify-end">
          <div className="flex flex-col max-w-md mx-2 items-end">
            {renderFileAttachment(true)}
            <div className="relative">
              {/* Bubble untuk pesan pengirim */}
              <div className="px-4 py-3 rounded-2xl rounded-br-md bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-md">
                <div className="mb-2">
                  <h6 className="font-semibold text-sm opacity-90 text-white">
                    {item.created_by?.name || "You"}
                  </h6>
                  <span className="text-xs opacity-75">
                    {dayjs(item.created_at).format("DD MMMM YYYY HH:mm")}
                  </span>
                </div>
                {item.chat && (
                  <div className="text-sm leading-relaxed">{item.chat}</div>
                )}
                <div className="flex mt-2 justify-end">
                  <span
                    className="text-xs cursor-pointer opacity-75 hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(item.uuid)}
                  >
                    Hapus
                  </span>
                </div>
              </div>
              {/* Tail untuk bubble */}
              {/* <div className="absolute bottom-0 right-0 w-0 h-0 border-l-[10px] border-l-transparent border-t-[10px] border-t-sky-600"></div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Pesan dari user lain (Penerima)
  else if (item?.created_by?.id) {
    return (
      <div key={`${item.uuid}`} className="chat-message mb-4">
        <div className="flex items-end">
          <div className="flex flex-col max-w-md mx-2 items-start">
            {renderFileAttachment(false)}
            <div className="relative">
              {/* Bubble untuk pesan penerima */}
              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-gray-200 text-gray-800 shadow-md">
                <div className="mb-2">
                  <h6 className="font-medium text-sm text-gray-700">
                    {item.created_by?.name || "Unknown"}
                  </h6>
                  <span className="text-xs text-gray-500">
                    {dayjs(item.created_at).format("DD MMMM YYYY HH:mm")}
                  </span>
                </div>
                {item.chat && (
                  <div className="text-sm leading-relaxed">{item.chat}</div>
                )}
              </div>
              {/* Tail untuk bubble */}
              {/* <div className="absolute bottom-0 left-0 w-0 h-0 border-r-[10px] border-r-transparent border-t-[10px] border-t-gray-200"></div> */}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

ChatBox.propTypes = {
  id: PropTypes.string.isRequired,
  claimData: PropTypes.object.isRequired,
};

export default ChatBox;
